import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Row, Col, Form } from "react-bootstrap";
import { formatDate } from "../../../../helpers/commonHelper";

const TreatmentClinicalSummary = ({
  step1Data,
  step2Data,
  step3Data,
  step4Data,
  step5Data,
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState("panel1");

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  return (
    <>
      <Accordion
        expanded={expandedAccordion === "panel1"}
        onChange={handleAccordionChange("panel1")}
        className="custom-acc"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className="tp-section-title">
            Diagnostic Details
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <div className="upload-clinical">
            {/* Section Title */}

            {/* Date Field */}
            <div className="row">
              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">Date of diagnostic</label>
                <p className="view-value">
                  {formatDate(step1Data?.date_of_diagnosis)}
                </p>
              </div>
            </div>

            {/* Cancer Info Row */}
            <div className="row mb-4">
              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">
                  Primary Site of Tumour/Topography
                </label>
                <p className="view-value">
                  {step1Data?.primary_tumour_site || "N/A"}
                </p>
              </div>

              <div className="col-md-2 col-12 mb-3">
                <label className="form-label">Histology/Morphology</label>
                <p className="view-value">
                  {step1Data?.histology_morphology_description || "N/A"}
                </p>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">
                  Coding According to ICD-O-3
                </label>
                <p className="view-value">
                  {step1Data?.icdo3_coding || "N/A"}
                </p>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">
                  Secondary Site of Tumour
                </label>
                <p className="view-value">
                  {step1Data?.secondary_tumour_site || "N/A"}
                </p>
              </div>
            </div>

            {/* Morphology + Laterality */}
            <div className="row mb-4 align-items-center">
              {/* Morphology of Metastasis */}
              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">
                  Morphology of Metastasis
                </label>
                <p className="view-value">
                  {step1Data?.metastasis_morphology || "—"}
                </p>
              </div>

              {/* Laterality Selection */}
              <div className="col-md-4 col-12 mb-3">
                <label className="form-label fw-semibold d-block mb-2">
                  Laterality
                </label>
                <p className="view-value">
                  {step1Data?.is_laterality === 1 ? "Yes" : "No"}
                </p>
              </div>

              {/* Laterality Dropdown Value */}
              {step1Data?.is_laterality === 1 && (
                <div className="col-md-3 col-12 mb-3">
                  <label className="form-label">Laterality Details</label>
                  <p className="view-value">
                    {step1Data?.laterality || "N/A"}
                  </p>
                </div>
              )}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "panel2"}
        onChange={handleAccordionChange("panel2")}
        className="custom-acc"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className="tp-section-title">
            Clinical Stage
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div className="upload-clinical">
            {/* Title */}
            {/* Clinical Extent of Disease */}
            <div className="row mb-4 align-items-center">
              <div className="col-md-3 col-12 mt-4">
                <label className="form-label">
                  Clinical Extent of Disease
                </label>
                <p className="view-value">
                  {step2Data?.disease_extent || "N/A"}
                </p>
              </div>

              {/* If “Others” Selected */}
              {step2Data?.disease_extent === "Others" && (
                <div className="col-md-4 col-12">
                  <label className="form-label"></label>
                  <p className="view-value">
                    {step2Data?.disease_extent_other || "N/A"}
                  </p>
                </div>
              )}
            </div>

            {/* ---------- STAGING SYSTEM ---------- */}
            <h5 className="mb-3">Staging System</h5>

            {/* Clinical Stage Group */}
            <Typography variant="p" sx={{ fontWeight: 400 }}>
              Clinical Stage Group
            </Typography>

            <div className="row">
              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">T (Tumor)</label>
                <p className="view-value">
                  {step2Data?.clinical_t || "N/A"}
                </p>
              </div>

              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">N (Nodes)</label>
                <p className="view-value">
                  {step2Data?.clinical_n || "N/A"}
                </p>
              </div>

              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">M (Metastasis)</label>
                <p className="view-value">
                  {step2Data?.clinical_m || "N/A"}
                </p>
              </div>
            </div>

            {/* Pathological Stage Group */}
            <div className="row mt-5">
              <Typography variant="p" sx={{ fontWeight: 400 }}>
                Pathological Stage Group
              </Typography>

              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">T (Tumor)</label>
                <p className="view-value">
                  {step2Data?.pathological_t || "N/A"}
                </p>
              </div>

              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">N (Nodes)</label>
                <p className="view-value">
                  {step2Data?.pathological_n || "N/A"}
                </p>
              </div>

              <div className="col-md-2 col-lg-3 col-12">
                <label className="form-label">M (Metastasis)</label>
                <p className="view-value">
                  {step2Data?.pathological_m || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "panel3"}
        onChange={handleAccordionChange("panel3")}
        className="custom-acc"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className="tp-section-title">
            Chemotherapy Administration Details
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box className="tp-section">
            {/* ECOG Status */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>ECOG Status</label>
                <p className="view-value">
                  {step3Data?.ecog_status || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Start Date</label>
                <p className="view-value">
                  {formatDate(step3Data?.date_of_start)}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>End Date</label>
                <p className="view-value">
                  {formatDate(step3Data?.date_of_end)}
                </p>
              </div>
            </Box>

            {/* Intention / Role / Details */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Intention to Treatment</label>
                <p className="view-value">
                  {step3Data?.intention_of_treatment || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Role of Treatment</label>
                <p className="view-value">
                  {step3Data?.role_of_treatment || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Details</label>
                <p className="view-value">
                  {step3Data?.details || "N/A"}
                </p>
              </div>
            </Box>

            {/* Status */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Status of Treatment</label>
                <p className="view-value">
                  {step3Data?.status_of_treatment || "N/A"}
                </p>
              </div>
            </Box>

            {/* Surgery */}
            <h5 className="my-3">Surgery</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Date of Surgery</label>
                <p className="view-value">
                  {formatDate(step3Data?.date_of_surgery)}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Primary Site</label>
                <p className="view-value">
                  {step3Data?.primary_site || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Surgeon Name</label>
                <p className="view-value">
                  {step3Data?.surgeon_name || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Surgical Procedure Performed</label>
                <p className="view-value">
                  {step3Data?.surgical_procedure_performed || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Extent Resection</label>
                <p className="view-value">
                  {step3Data?.extent_of_resection || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Tumour Margins</label>
                <p className="view-value">
                  {step3Data?.tumor_margins || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Lymph Node Dissection</label>
                <p className="view-value">
                  {step3Data?.lymph_node_dissection || "N/A"}
                </p>
              </div>
            </Box>

            {/* Chemotherapy */}
            <h5 className="my-3">Chemotherapy</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Regimen</label>
                <p className="view-value">
                  {step3Data?.chemo_regimen || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Protocol with Duration</label>
                <p className="view-value">
                  {step3Data?.chemo_protocol_with_duration || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Number of Cycles Planned</label>
                <p className="view-value">
                  {step3Data?.chemo_planned_cycles || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Number of Cycles Administered</label>
                <p className="view-value">
                  {step3Data?.chemo_cycles_administered || "N/A"}
                </p>
              </div>
            </Box>

            {/* Radiotherapy */}
            <h5 className="my-3">Radiotherapy</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Regimen</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_regimen || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Protocol Name</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_protocol_name || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Intent</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_treatment_intent || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Total Dose</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_total_dose || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Dose per Fraction</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_dose_per_fraction || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Site</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_treatment_site || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Number of Fractions Planned</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_no_of_planned_fractions ||
                    "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Number of Fractions Administered</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_no_of_fractions_administered ||
                    "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Radiation Modality</label>
                <p className="view-value">
                  {step3Data?.radiotherapy_radiation_modality || "N/A"}
                </p>
              </div>
            </Box>

            {/* Targeted Therapy */}
            <h5 className="my-3">Targeted Therapy</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Regimen/Protocol Name</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_regimen_protocol || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Intent</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_treatment_intent || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Drug</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_targeted_drug || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Molecular Target</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_molecular_target || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Specific Dose</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_specific_dose || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Route of Administration</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_route_of_administration ||
                    "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Cycles (Planned)</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_planned_cycles || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Route of Administration</label>
                <p className="view-value">
                  {step3Data?.targeted_therapy_administered_cycles ||
                    "N/A"}
                </p>
              </div>
            </Box>

            {/* Immunotherapy */}
            <h5 className=" my-3">Immunotherapy</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Regimen</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_regimen || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Protocol Name</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_protocol_name || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Intent</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_treatment_intent || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Immunotherapy Drug</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_targeted_drug || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Mechanism of Action</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_molecular_target || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Specific Dose</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_specific_dose || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Route of Administration</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_route_of_administration ||
                    "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Number of Cycles Planned</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_planned_cycles || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Number of Cycles Administered</label>
                <p className="view-value">
                  {step3Data?.immunotherapy_administered_cycles || "N/A"}
                </p>
              </div>
            </Box>

            {/* Hormone Therapy */}
            <h5 className="my-3">Hormone Therapy</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Regimen/Protocol Name</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_regimen_protocol || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Treatment Intent</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_treatment_intent || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Hormone Therapy Drug</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_immunotherapy || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Mechanism of Action</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_mechanism_of_action ||
                    "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Specific Dose</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_specific_dose || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Route of Administration</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_route_of_administration ||
                    "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Cycles Planned</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_planned_cycles || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Cycles Administered</label>
                <p className="view-value">
                  {step3Data?.hormone_therapy_cycles_administered ||
                    "N/A"}
                </p>
              </div>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "panel4"}
        onChange={handleAccordionChange("panel4")}
        className="custom-acc"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className="tp-section-title">
            Premedications
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box className="tp-section">
            {/* TITLE */}
            <h5>Current Chemotherapy Protocol</h5>

            {/* ROW 1 */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Chemotherapy Regimen Details</label>
                <p className="view-value">
                  {step4Data?.chemo_regimen_details || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Intent</label>
                <p className="view-value">
                  {step4Data?.treatment_intent || "N/A"}
                </p>
              </div>
            </Box>

            {/* TITLE */}
            <h5>Treatment Provided</h5>

            {/* ---- ROW 1 ---- */}
            {step4Data?.treatments.map((data, idx) => (
              <Box className="tp-row" key={idx + 1}>
                <div className="tp-col-cust">
                  <label>Date</label>
                  <p className="view-value">
                    {formatDate(data?.chemo_administered_date)}
                  </p>
                </div>

                <div className="tp-col-cust">
                  <label>Dose {idx + 1}</label>
                  <p className="view-value">{data?.chemo_dosage}</p>
                </div>
              </Box>
            ))}

            {/* TITLE */}
            <h5>Drug Allergies & Access</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Known Drug Allergies</label>
                <p className="view-value">
                  {step4Data?.known_drug_allergies || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Vascular Access</label>
                <p className="view-value">
                  {step4Data?.vascular_access || "N/A"}
                </p>
              </div>
            </Box>

            {/* TITLE */}
            <h5>Adverse Reactions</h5>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Significant Adverse Reactions Reported</label>
                <p className="view-value">
                  {step4Data?.significant_adverse_reactions_reported ||
                    "N/A"}
                </p>
              </div>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expandedAccordion === "panel5"}
        onChange={handleAccordionChange("panel5")}
        className="custom-acc"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className="tp-section-title">
            Instruction for DCCC while referring Patients
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box className="tp-section">
            {/* Date of Referral */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Date of Referral</label>
                <p className="view-value">
                  {formatDate(step5Data?.referral_date)}
                </p>
              </div>
            </Box>

            {/* Referred to DCCC */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>State</label>
                <p className="view-value">
                  {step5Data?.state_name?.state_name}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>District</label>
                <p className="view-value">
                  {step5Data?.district_name?.district_name}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>DCCC</label>
                <p className="view-value">
                  {step5Data?.dccc_name?.dccc_name}
                </p>
              </div>
            </Box>

            {/* TITLE */}
            <h5>Chemotherapy Drugs for Upcoming Cycles</h5>
            {/* Chemotherapy Drugs */}
            {step5Data?.drug_details.map((data, idx) => (
              <Box className="tp-row" key={idx}>
                <div className="tp-col-cust">
                  <label>Drug Name</label>
                  <p className="view-value">{data?.drug_name || "N/A"}</p>
                </div>

                <div className="tp-col-cust">
                  <label>Dosage (per Kg/flat dose)</label>
                  <p className="view-value">{data?.dosage || "N/A"}</p>
                </div>

                <div className="tp-col-cust">
                  <label>Final Calculated Dose</label>
                  <p className="view-value">
                    {data?.final_dose || "N/A"}
                  </p>
                </div>

                <div className="tp-col-cust">
                  <label>Route of Administration</label>
                  <p className="view-value">
                    {data?.route_admin || "N/A"}
                  </p>
                </div>
              </Box>
            ))}

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Number of Cycles Planned</label>
                <p className="view-value">
                  {step5Data?.planned_chemo_cycles || "N/A"}
                </p>
              </div>
            </Box>

            {/* Chemotherapy Administration Details */}
            <h5>Chemotherapy Administration Details</h5>
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Infusion solution/dilution</label>
                <p className="view-value">
                  {step5Data?.chemo_infusion_rate_duration || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Diluent volume</label>
                <p className="view-value">
                  {step5Data?.chemo_diluent_volume || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Timing</label>
                <p className="view-value">
                  {step5Data?.chemo_timing || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Delivery Method</label>
                <p className="view-value">
                  {step5Data?.chemo_delivery_method || "N/A"}
                </p>
              </div>
            </Box>

            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Notes/Special Instructions</label>
                <p className="view-value">
                  {step5Data?.chemo_notes_special_instructions || "N/A"}
                </p>
              </div>
            </Box>

            {/* Premedications */}
            <h5>Premedications</h5>

            {step5Data?.premedication_details.map((data, idx) => (
              <Box className="tp-row" key={idx}>
                <div className="tp-col-cust">
                  <label>Drug Type</label>
                  <p className="view-value">{data?.drug_type || "N/A"}</p>
                </div>

                <div className="tp-col-cust">
                  <label>Drug Name</label>
                  <p className="view-value">{data?.drug_name || "N/A"}</p>
                </div>

                <div className="tp-col-cust">
                  <label>Dose</label>
                  <p className="view-value">{data?.drug_dose || "N/A"}</p>
                </div>

                <div className="tp-col-cust">
                  <label>Route of Administration</label>
                  <p className="view-value">
                    {data?.administration_route || "N/A"}
                  </p>
                </div>
              </Box>
            ))}

            {/* Hydration Plan */}
            <h5>Hydration Plan</h5>
            <h6>Pre-Chemo Hydration</h6>
            {/* Pre-Chemo Hydration */}
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Fluid Type</label>
                <p className="view-value">
                  {step5Data?.pre_chemo_hydration_fluid_type || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Volume</label>
                <p className="view-value">
                  {step5Data?.pre_chemo_hydration_fluid_volume || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Duration</label>
                <p className="view-value">
                  {step5Data?.pre_chemo_hydration_fluid_duration || "N/A"}
                </p>
              </div>
            </Box>

            {/* Post-Chemo Hydration */}
            <h6>Post-Chemo Hydration</h6>
            <Box className="tp-row">
              <div className="tp-col-cust">
                <label>Post-Chemo Hydration Type</label>
                <p className="view-value">
                  {step5Data?.post_chemo_hydration_fluid_type || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Volume</label>
                <p className="view-value">
                  {step5Data?.post_chemo_hydration_fluid_volume || "N/A"}
                </p>
              </div>

              <div className="tp-col-cust">
                <label>Duration</label>
                <p className="view-value">
                  {step5Data?.post_chemo_hydration_fluid_duration ||
                    "N/A"}
                </p>
              </div>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default TreatmentClinicalSummary;
